import { baseApi, apiTags } from "../baseApi";
import type {
    Application,
    ApplicationListParams,
    ApplicationByIdParams,
    UpdateApplicationParams,
    UpdateApplicationFieldParams,
} from "../types/api.types";
import type { LaravelQueryBuilder } from '../types/laravelRequest';

export const applicationsApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        getApplications: builder.query<Application[], ApplicationListParams>({
            query: ({ isArchive }) => ({
                resource: "crm-application",
                action: "index",
                method: "GET",
                useAll: true,
                queryBuilder: (query: LaravelQueryBuilder) => {
                    query
                        .with("resource")
                        .with("refuseReason")
                        .with("statusHistory")
                        .with("mainContact")
                        .with("assignedContacts")
                        .with("assignedContacts.contact");

                    query.where("crm_applications.is_archive", isArchive ? 1 : 0);

                    return query;
                },
            }),
            providesTags: (result) =>
                result && Array.isArray(result)
                    ? [
                          { type: apiTags.Application, id: "LIST" },
                          ...result.map((item) => ({ type: apiTags.Application, id: item.id })),
                      ]
                    : [{ type: apiTags.Application, id: "LIST" }],
        }),
        getApplicationById: builder.query<Application, ApplicationByIdParams>({
            query: ({ id, isArchive }) => ({
                resource: "crm-application",
                action: "index",
                method: "GET",
                id: id,
                queryBuilder: (query: LaravelQueryBuilder) => {
                    return query
                        .where("id", id)
                        .where("crm_applications.is_archive", isArchive ? 1 : 0)
                        .with("resource")
                        .with("refuseReason")
                        .with("statusHistory")
                        .with("mainContact")
                        .with("assignedContacts")
                        .with("assignedContacts.contact")
                        .first();
                },
            }),
            providesTags: (result, error, arg) => (result ? [{ type: apiTags.Application, id: arg.id }] : []),
        }),
        updateApplication: builder.mutation<Application, UpdateApplicationParams>({
            query: ({ id, data }) => ({
                resource: "crm-application",
                action: "update",
                method: "PUT",
                id: id,
                data: data,
            }),
            invalidatesTags: (result, error, arg) => [
                { type: apiTags.Application, id: arg.id },
                { type: apiTags.Application, id: "LIST" },
            ],
        }),
        updateApplicationField: builder.mutation<Application, UpdateApplicationFieldParams>({
            query: ({ id, field, value }) => ({
                resource: "crm-application",
                action: "update-field",
                method: "PUT",
                id: id,
                data: { [field]: value },
            }),
            invalidatesTags: (result, error, arg) => [
                { type: apiTags.Application, id: arg.id },
                { type: apiTags.Application, id: "LIST" },
            ],
        }),
    }),
});

export const {
    useGetApplicationsQuery,
    useGetApplicationByIdQuery,
    useLazyGetApplicationByIdQuery,
    useUpdateApplicationMutation,
    useUpdateApplicationFieldMutation,
} = applicationsApi;
